import { useState } from 'react';
import { SliderItemConfig } from '@/lib/themeConfig';
import { SaveButton } from './GeneralSection';
import ImageUploader from '../ImageUploader';

interface Props {
  data: SliderItemConfig[];
  onChange: (data: SliderItemConfig[]) => void;
}

const emptySlide = (): SliderItemConfig => ({
  id: Date.now().toString(),
  title: '',
  subtitle: '',
  description: '',
  cta_text: 'Keşfet',
  cta_link: '/urunler',
  image_url: '',
  badge: '',
});

export default function SliderSection({ data, onChange }: Props) {
  const [slides, setSlides] = useState<SliderItemConfig[]>(data);
  const [activeIdx, setActiveIdx] = useState(0);
  const [saved, setSaved] = useState(false);

  const updateSlide = (idx: number, key: keyof SliderItemConfig, value: string) => {
    setSlides(prev => prev.map((s, i) => i === idx ? { ...s, [key]: value } : s));
  };

  const addSlide = () => {
    const next = [...slides, emptySlide()];
    setSlides(next);
    setActiveIdx(next.length - 1);
  };

  const removeSlide = (idx: number) => {
    if (slides.length <= 1) return;
    const next = slides.filter((_, i) => i !== idx);
    setSlides(next);
    setActiveIdx(Math.min(activeIdx, next.length - 1));
  };

  const handleSave = () => {
    onChange(slides);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const slide = slides[activeIdx];

  return (
    <div className="max-w-3xl space-y-6">
      {/* Slide Tabs */}
      <div className="bg-white rounded-2xl p-4">
        <div className="flex items-center gap-2 flex-wrap">
          {slides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActiveIdx(i)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeIdx === i ? 'bg-amber-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Slayt {i + 1}
            </button>
          ))}
          <button
            onClick={addSlide}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
          >
            <i className="ri-add-line" /> Yeni Slayt
          </button>
        </div>
      </div>

      {/* Slide Editor */}
      {slide && (
        <div className="bg-white rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-stone-700">Slayt {activeIdx + 1}</h2>
            {slides.length > 1 && (
              <button
                onClick={() => removeSlide(activeIdx)}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line" /> Sil
              </button>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Rozet (opsiyonel)</label>
            <input
              type="text"
              value={slide.badge}
              onChange={e => updateSlide(activeIdx, 'badge', e.target.value)}
              placeholder="2026 Yeni Sezon"
              className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Başlık <span className="text-stone-400">(yeni satır için \n kullanın)</span></label>
            <textarea
              value={slide.title}
              onChange={e => updateSlide(activeIdx, 'title', e.target.value)}
              rows={2}
              placeholder="Kahvenin En Saf\nHali"
              className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1.5">Açıklama</label>
            <textarea
              value={slide.description}
              onChange={e => updateSlide(activeIdx, 'description', e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Buton Metni</label>
              <input
                type="text"
                value={slide.cta_text}
                onChange={e => updateSlide(activeIdx, 'cta_text', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1.5">Buton Linki</label>
              <input
                type="text"
                value={slide.cta_link}
                onChange={e => updateSlide(activeIdx, 'cta_link', e.target.value)}
                placeholder="/urunler"
                className="w-full px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
              />
            </div>
          </div>

          <ImageUploader
            label="Arka Plan Görseli"
            value={slide.image_url}
            onChange={val => updateSlide(activeIdx, 'image_url', val)}
            previewHeight="h-40"
            hint="Önerilen boyut: 1920×1080px"
          />
        </div>
      )}

      <SaveButton saved={saved} onSave={handleSave} />
    </div>
  );
}
